import { API_KEYS, API_ENDPOINTS, IMAGE_CONFIG, MODELS } from './constants';
import { uploadImageToR2, blobUrlToBlob, generateFilename } from './imageUploadService';

// Generate images using OpenAI GPT-Image-1
export const generateOpenAIImages = async (prompt, numImages = IMAGE_CONFIG.OPENAI_COUNT) => {
  try {
    console.log(`🤖 Generating ${numImages} images with OpenAI GPT-Image-1`);

    // Check if OpenAI is enabled and key is valid
    if (!IMAGE_CONFIG.USE_OPENAI || API_KEYS.OPENAI_API_KEY === "YOUR_NEW_OPENAI_KEY_HERE") {
      console.warn('⚠️ OpenAI is disabled or key is invalid, skipping OpenAI generation');
      return [];
    }

    const imageUrls = [];

    // OpenAI API request
    const response = await fetch(API_ENDPOINTS.OPENAI_GENERATE, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${API_KEYS.OPENAI_API_KEY}`
      },
      body: JSON.stringify({
        model: MODELS.OPENAI_MODEL,
        prompt: prompt,
        n: numImages,
        size: IMAGE_CONFIG.IMAGE_SIZE,
        quality: IMAGE_CONFIG.QUALITY,
        response_format: 'b64_json'
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('❌ OpenAI API error:', errorData);
      throw new Error(`OpenAI API failed: ${response.status} - ${errorData.error?.message || 'Unknown error'}`);
    }

    const data = await response.json();

    // Process each generated image
    for (let i = 0; i < data.data.length; i++) {
      const imageData = data.data[i];

      if (imageData.b64_json) {
        try {
          // Convert base64 to blob
          const binaryString = atob(imageData.b64_json);
          const bytes = new Uint8Array(binaryString.length);
          for (let j = 0; j < binaryString.length; j++) {
            bytes[j] = binaryString.charCodeAt(j);
          }
          const blob = new Blob([bytes], { type: 'image/png' });

          // Generate filename and upload to R2
          const filename = generateFilename('openai_gpt_image_1', i + 1);
          console.log(`📤 [OPENAI ${i + 1}] Uploading to R2 with filename: ${filename}`);

          const httpUrl = await uploadImageToR2(blob, filename);
          console.log(`✅ [OPENAI ${i + 1}] Upload successful, R2 URL: ${httpUrl}`);

          imageUrls.push({
            url: httpUrl,
            source: 'OpenAI',
            index: i + 1,
            prompt: prompt,
            timestamp: new Date().toISOString(),
            uploadStatus: 'success'
          });
        } catch (uploadError) {
          console.error(`❌ [OPENAI ${i + 1}] Upload failed:`, uploadError);
          // In production, skip failed uploads
          const isProduction = process.env.NODE_ENV === 'production';
          if (!isProduction) {
            console.warn(`⚠️ [OPENAI ${i + 1}] Development mode: continuing despite error`);
          }
        }
      }
    }

    console.log(`✅ Generated ${imageUrls.length}/${numImages} images with OpenAI`);
    return imageUrls;

  } catch (error) {
    console.error('❌ OpenAI image generation failed:', error);
    // Return empty array on error - let Gemini handle all images
    return [];
  }
};

// Generate images using Gemini 2.5 Flash Image Preview (one by one)
export const generateGeminiImages = async (prompt, numImages = IMAGE_CONFIG.GEMINI_COUNT) => {
  try {
    console.log(`🤖 Generating ${numImages} images with Gemini 2.5 Flash (one by one)`);
    
    const imageUrls = [];
    // Encourage diversity across the 4 generations
    const VARIANTS = [
      'wide-angle composition, natural daylight, warm color palette, soft shadows',
      'macro close-up texture details, studio lighting, cool palette, high micro-contrast',
      'isometric three-quarter view, ambient soft lighting, neutral palette, minimal clutter',
      'dynamic low-angle perspective, golden-hour lighting, bold contrast, rich highlights'
    ];
    
    // Generate images one by one
    for (let i = 0; i < numImages; i++) {
      console.log(`🔄 Generating Gemini image ${i + 1}/${numImages}...`);
      
      try {
        const nonce = Math.random().toString(36).slice(2);
        const variantHint = VARIANTS[i % VARIANTS.length];
        const imagePrompt = `Generate a high-quality image: ${prompt}. Variation focus: ${variantHint}. Ensure this variation differs clearly from other versions in composition, angle, lighting, and framing. Nonce: ${nonce}.`;
        
        const response = await fetch(`${API_ENDPOINTS.GEMINI_GENERATE}?key=${API_KEYS.GEMINI_API_KEY}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: imagePrompt
              }]
            }],
            generationConfig: {
              temperature: 0.9,
              topP: 0.8,
              maxOutputTokens: 2048
            }
          })
        });

        if (!response.ok) {
          let errorText = '';
          try {
            const errJson = await response.json();
            console.error(`❌ Gemini ${i + 1} full error:`, errJson);
            errorText = errJson && errJson.error ? `${errJson.error.code || ''} ${errJson.error.status || ''} - ${errJson.error.message || ''}` : JSON.stringify(errJson);
          } catch (_) {
            try {
              errorText = await response.text();
              console.error(`❌ Gemini ${i + 1} error text:`, errorText);
            } catch (_) {
              errorText = '';
            }
          }
          console.error(`❌ Gemini image ${i + 1} failed: ${response.status}${errorText ? ` | ${errorText}` : ''}`);
          continue;
        }

        const data = await response.json();
        
        // Extract image data from Gemini response
        if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
          const imagePart = data.candidates[0].content.parts.find(part => part.inlineData);
          if (imagePart && imagePart.inlineData) {
            // Convert base64 to blob URL
            const imageData = imagePart.inlineData.data;
            const mimeType = imagePart.inlineData.mimeType || 'image/png';
            const blob = new Blob([Uint8Array.from(atob(imageData), c => c.charCodeAt(0))], { type: mimeType });
            const imageUrl = URL.createObjectURL(blob);
            
            // Upload to R2 storage to get proper HTTP URL
            try {
              console.log(`📤 [IMAGE ${i + 1}] Converting blob URL to blob for upload...`);
              const blob = await blobUrlToBlob(imageUrl);
              console.log(`✅ [IMAGE ${i + 1}] Blob created: ${blob.size} bytes, type: ${blob.type}`);

              const filename = generateFilename('gemini_2_5_flash', i + 1);
              console.log(`📤 [IMAGE ${i + 1}] Uploading to R2 with filename: ${filename}`);

              const httpUrl = await uploadImageToR2(blob, filename);
              console.log(`✅ [IMAGE ${i + 1}] Upload successful, R2 URL: ${httpUrl}`);

              // Verify the URL is a proper HTTP URL, not a blob URL
              if (!httpUrl.startsWith('http://') && !httpUrl.startsWith('https://')) {
                throw new Error(`Invalid URL returned from upload: ${httpUrl}`);
              }

              imageUrls.push({
                url: httpUrl, // Now it's a proper HTTP URL
                source: 'Gemini',
                index: i + 1,
                prompt: imagePrompt,
                timestamp: new Date().toISOString(),
                uploadStatus: 'success'
              });
              console.log(`✅ [IMAGE ${i + 1}] Gemini image generated and uploaded successfully`);
            } catch (uploadError) {
              console.error(`❌ [IMAGE ${i + 1}] UPLOAD FAILED:`, uploadError);
              console.error(`❌ [IMAGE ${i + 1}] Error type:`, uploadError.constructor.name);
              console.error(`❌ [IMAGE ${i + 1}] Error message:`, uploadError.message);
              console.error(`❌ [IMAGE ${i + 1}] Error stack:`, uploadError.stack);

              // In production, we should NOT fall back to blob URLs
              // Blob URLs don't persist and won't work after page reload
              const isProduction = process.env.NODE_ENV === 'production';

              if (isProduction) {
                console.error(`❌ [IMAGE ${i + 1}] PRODUCTION: Upload failed, skipping this image`);
                // Don't add the image to the array in production if upload fails
                // This ensures we never save blob URLs to the database
              } else {
                console.warn(`⚠️ [IMAGE ${i + 1}] DEVELOPMENT: Falling back to blob URL for testing`);
                imageUrls.push({
                  url: imageUrl, // Fallback to blob URL in development only
                  source: 'Gemini',
                  index: i + 1,
                  prompt: imagePrompt,
                  timestamp: new Date().toISOString(),
                  uploadStatus: 'failed_blob_fallback'
                });
              }
            }
          }
        }
        
        // Add small delay between requests
        if (i < numImages - 1) {
          await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay for Gemini
        }
        
      } catch (imageError) {
        console.error(`❌ Error generating Gemini image ${i + 1}:`, imageError);
        // Continue with next image
      }
    }
    
    console.log(`✅ Generated ${imageUrls.length}/${numImages} images with Gemini`);
    return imageUrls;
    
  } catch (error) {
    console.error('❌ Gemini image generation failed:', error);
    throw error;
  }
};

// Main image generation function with progress tracking
export const generateImages = async (formData, onProgress = null) => {
  try {
    const prompt = formData.prompt || '';
    const numImages = formData.num_images || IMAGE_CONFIG.DEFAULT_COUNT;
    const category = (formData.category || '').toLowerCase();
    
    console.log('🔍 Form data received:', formData);
    console.log('🔍 Category:', formData.category);
    console.log('🔍 Subtype:', formData.subtype);
    
    if (!prompt.trim()) {
      throw new Error('Prompt is required');
    }
    
    // Category-based prompt shaping
    const isSwatchCategory = /floor|flooring|finish|finishes/.test(category);
    const isInspirationCategory = /interior|exterior/.test(category);

    let styledPrompt = prompt;
    if (isSwatchCategory) {
      styledPrompt = `Seamless material swatch texture tile, 1024x1024, top-down orthographic view, neutral white studio lighting, sharp details, no perspective, no environment, no background, edge-to-edge coverage, perfectly tiling and repeatable, photorealistic PBR quality: ${prompt}. Ensure seamless tiling with no visible seams.`;
    } else if (isInspirationCategory) {
      styledPrompt = `Ultra high quality ${category.includes('exterior') ? 'exterior' : 'interior'} marketing inspiration image, photorealistic, 4K, professional composition, realistic lighting, refined materials, crisp focus, cinematic tones, no text, no watermark: ${prompt}.`;
    }

    console.log(`🎨 Generating ${numImages} images with styled prompt:`, styledPrompt);
    console.log(`🎨 Hybrid mode: OpenAI enabled = ${IMAGE_CONFIG.USE_OPENAI}`);

    // Optional progress callback
    if (onProgress) {
      onProgress({ stage: 'starting', progress: 0, total: numImages });
    }

    let allImages = [];

    // Hybrid generation: Try OpenAI first (if enabled), then Gemini
    if (IMAGE_CONFIG.USE_OPENAI) {
      console.log(`🎨 Hybrid mode: Generating ${IMAGE_CONFIG.OPENAI_COUNT} OpenAI + ${IMAGE_CONFIG.GEMINI_COUNT} Gemini images`);

      // Generate OpenAI images (2 images)
      try {
        const openaiImages = await generateOpenAIImages(styledPrompt, IMAGE_CONFIG.OPENAI_COUNT);
        allImages = allImages.concat(openaiImages);
        console.log(`✅ OpenAI generation complete: ${openaiImages.length} images`);

        if (onProgress) {
          onProgress({ stage: 'openai_complete', progress: openaiImages.length, total: numImages });
        }
      } catch (openaiError) {
        console.error('❌ OpenAI generation failed, will use Gemini for all images:', openaiError);
      }

      // Generate Gemini images (2 images, or 4 if OpenAI failed)
      const geminiCount = allImages.length > 0 ? IMAGE_CONFIG.GEMINI_COUNT : numImages;
      console.log(`🎨 Generating ${geminiCount} Gemini images`);

      const geminiImages = await generateGeminiImages(styledPrompt, geminiCount);
      allImages = allImages.concat(geminiImages);
      console.log(`✅ Gemini generation complete: ${geminiImages.length} images`);

    } else {
      // Gemini-only generation when OpenAI is disabled
      console.log(`🎨 Gemini-only mode: Generating ${numImages} Gemini images`);
      allImages = await generateGeminiImages(styledPrompt, numImages);
    }

    // Enhance images with form data
    const enhancedImages = allImages.map((image, index) => ({
      ...image,
      subcategory: formData.subtype || "",
      tags: formData.keywords ? formData.keywords.split(',').map(k => k.trim()).filter(k => k) : [],
      category: formData.category || "general",
      render_type: isSwatchCategory ? 'swatch' : (isInspirationCategory ? 'inspiration' : 'general'),
      generated_at: new Date().toISOString(),
      final_index: index + 1
    }));

    console.log(`✅ Total images generated: ${enhancedImages.length} (${enhancedImages.filter(i => i.source === 'OpenAI').length} OpenAI + ${enhancedImages.filter(i => i.source === 'Gemini').length} Gemini)`);

    if (onProgress) {
      onProgress({ stage: 'complete', progress: enhancedImages.length, total: numImages });
    }

    return {
      images: enhancedImages,
      total_generated: enhancedImages.length,
      prompt: prompt,
      category: formData.category || "general",
      generation_time: new Date().toISOString(),
      breakdown: {
        openai: enhancedImages.filter(i => i.source === 'OpenAI').length,
        gemini: enhancedImages.filter(i => i.source === 'Gemini').length
      }
    };
    
  } catch (error) {
    console.error('❌ Image generation failed:', error);
    throw error;
  }
};